import { useActionSheet } from '@expo/react-native-action-sheet'
import {
  Conversation,
  ConversationType,
  Id,
  MessageAction,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useChatConfig } from 'chat/config/queries/get'
import ConversationsView from 'chat/conversations/views'
import { useNonDeletedMessages } from 'chat/messages/queries/list'
import { useMentionsUnreadCount } from 'chat/messages/queries/mentions'
import { useUnmarkMessage } from 'chat/messages/queries/unmark'
import { useMessagingContext } from 'chat/messaging_context'
import { ConversationTabProps } from 'home/stack/types'
import { default as React, useCallback, useMemo, useState } from 'react'
import { ProcessingView } from 'shared/components/processing'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useRecentStoresContext } from 'stores/context'
import { useUser } from 'users/queries/detail'
import { useCreateCustomGroup } from './group/context'
import { useConversationsSections } from './queries/context'
import { useConversations } from './queries/list'
import {
  convertMessagesToConversations,
  hasUrgentUnreadMessages,
  isRepository,
  prioritizeConversations,
} from './utils'
import GroupTabSwitcher from './views/GroupTabSwitcher'

export function ConversationsScreen({
  navigation,
  route,
}: ConversationTabProps) {
  const screenType = route.name
  const { data: user } = useUser()
  const { homeStoreId } = useRecentStoresContext()
  const {
    data: conversations,
    isLoading,
    error,
    refetch: refetchConversationsAsync,
  } = useConversations()
  const { data: chatConfig } = useChatConfig()
  const {
    data: messages,
    isLoading: isMessagesLoading,
    refetch: refetchMessagesAsync,
  } = useNonDeletedMessages(data => data.messages)
  const conversationsSections = useConversationsSections()
  const { handleNavigation } = useRoutingContext()
  const unmarkMessageMutation = useUnmarkMessage()
  const actionSheet = useActionSheet()
  const customGroupContext = useCreateCustomGroup()
  const { progress } = useMessagingContext()

  const onUnmarkMessage = useCallback(
    async (conversationId: Id, messageId: Id) => {
      await unmarkMessageMutation.mutateAsync({
        conversationId,
        messageId,
      })
    },
    [unmarkMessageMutation]
  )

  const onUnmarkMessageAction = useCallback(
    (conversationItem: Conversation) => {
      if (conversationItem.status?.recentMessage?.id) {
        onUnmarkMessage(
          conversationItem.id,
          conversationItem.status?.recentMessage?.id
        ).catch(err => console.log(err))
      }
    },
    [onUnmarkMessage]
  )

  const onMessageAction = useCallback(
    (conversationItem: Conversation, action: MessageAction) => {
      switch (action.name) {
        case 'unmark':
          onUnmarkMessageAction(conversationItem)
          break
      }
    },
    [onUnmarkMessageAction]
  )

  const onLongPress = useCallback(
    (conversation: Conversation) => {
      if (screenType !== 'marked') return
      const actions: MessageAction[] = []
      actions.push({ name: 'unmark' })
      const options = actions.map(action => t(action.name))
      options.push(t('cancel'))
      const cancelButtonIndex = actions.length
      actionSheet.showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex,
        },
        async (index: number | undefined) => {
          if (index !== undefined && index !== cancelButtonIndex) {
            onMessageAction(conversation, actions[index])
          }
        }
      )
    },
    [actionSheet, onMessageAction, screenType]
  )

  const chatEnabled = user?.chat?.enabled

  const markedMessages = useMemo(
    () =>
      messages?.filter(
        m => m.isMarked && conversations?.find(c => c.id === m.conversationId)
      ),
    [messages, conversations]
  )

  const mentionedMessages = useMemo(
    () =>
      messages?.filter(m =>
        m.mentions?.find(mention => mention.user.id === user?.id)
      ),
    [messages, user]
  )

  const sentMessages = useMemo(() => {
    const filesGroupIds = conversationsSections
      .find(section => section.type === 'custom')
      ?.data.filter(c => isRepository(c))
      .map(c => c.id)
    return messages?.filter(
      m => m.user.id === user?.id && !filesGroupIds?.includes(m.conversationId)
    )
  }, [messages, user, conversationsSections])

  const mentionedConversations: Conversation[] = useMemo(
    () => convertMessagesToConversations(mentionedMessages, conversations),
    [mentionedMessages, conversations]
  )

  const markedConversations: Conversation[] = useMemo(
    () => convertMessagesToConversations(markedMessages, conversations),
    [markedMessages, conversations]
  )

  const sentConversations: Conversation[] = useMemo(
    () => convertMessagesToConversations(sentMessages, conversations),
    [sentMessages, conversations]
  )

  const onPressConversation = useCallback(
    (conversation: Conversation, messageId?: Id) => {
      if (isRepository(conversation)) {
        handleNavigation({
          routeName: 'file_folders_bottom_tab',
          routeParams: {
            screen: 'files',
            params: { conversationId: conversation.id },
            conversationId: conversation.id,
          },
        })
        return
      } else {
        handleNavigation({
          routeName: 'messages',
          routeParams: {
            id: conversation.id,
            messageId:
              screenType === 'marked' || screenType === 'sent'
                ? messageId
                : undefined,
            previousLastViewedTimestamp:
              conversation.status?.lastViewedTimestamp,
          },
        })
      }
    },
    [handleNavigation, screenType]
  )

  const defaultDmMessagesType =
    useMentionsUnreadCount() > 0 ? 'mentions' : 'messages'

  const [dmMessagesType, setDmMessagesType] = useState(defaultDmMessagesType)

  const onGoToStarRecipients = useCallback(() => {
    handleNavigation({
      routeName: 'star_recipients',
      routeParams: {
        type: 'award',
      },
    })
  }, [handleNavigation])

  const onCreateConversation = useCallback(
    (conversationType: ConversationType) => {
      switch (conversationType) {
        case 'custom':
          customGroupContext.setMembers({})
          customGroupContext.setSelectedMemberAliases(undefined)
          customGroupContext.setGroup(undefined)
          handleNavigation({ routeName: 'custom_group_stack' })
          break
        case 'dm':
          if (!chatEnabled) {
            handleNavigation({
              routeName: 'compose',
              routeParams: {
                storeId: homeStoreId,
              },
            })
          } else if (chatConfig?.useComposeMessage) {
            handleNavigation({ routeName: 'compose' })
          } else {
            handleNavigation({ routeName: 'recipients' })
          }
          break
        default:
      }
    },
    [
      chatConfig?.useComposeMessage,
      chatEnabled,
      customGroupContext,
      handleNavigation,
      homeStoreId,
    ]
  )

  const onRefresh = useCallback(async () => {
    await Promise.all([refetchConversationsAsync(), refetchMessagesAsync()])
  }, [refetchConversationsAsync, refetchMessagesAsync])

  const conversationProp: Conversation[] | undefined = useMemo(() => {
    let chatGroups: Conversation[] | undefined = []
    switch (screenType) {
      case 'dm':
        switch (dmMessagesType) {
          case 'messages':
            chatGroups = conversationsSections.find(
              a => a.type === 'direct'
            )?.data
            break
          case 'mentions':
            chatGroups = mentionedConversations
            break
        }
        break
      case 'groups':
        chatGroups = conversationsSections.find(a => a.type === 'group')?.data
        break
      case 'marked':
        chatGroups = markedConversations
        break
      case 'announcements':
        chatGroups = conversationsSections.find(
          a => a.type === 'announcements'
        )?.data
        break
      case 'sent':
        chatGroups = sentConversations
        break
      case 'allConversations':
        chatGroups = conversationsSections.flatMap(a => a.data)
        break
    }
    return chatGroups
  }, [
    screenType,
    dmMessagesType,
    conversationsSections,
    markedConversations,
    sentConversations,
    mentionedConversations,
  ])

  const urgentUnreadConversationIds = useMemo(() => {
    return conversationProp
      ?.filter(conversation => hasUrgentUnreadMessages(conversation, messages))
      ?.map(conversation => conversation.id)
  }, [conversationProp, messages])

  const sortedConversations = useMemo(() => {
    return prioritizeConversations(
      conversationProp ?? [],
      user,
      urgentUnreadConversationIds
    )
  }, [conversationProp, user, urgentUnreadConversationIds])

  if (isMessagesLoading) {
    return (
      <ProcessingView
        iconName={'message-processing-outline'}
        iconType={'material-design'}
        progress={progress}
        title={t('loadingChat')}
        description={t('loadingChatText')}
        processingText={t('loadingChatProgress', {
          current: progress.current,
          total: progress.total,
        })}
        downloadingText={t('downloadingChat')}
      />
    )
  }

  return (
    <StatusBoundary isLoading={isLoading} error={error}>
      {chatEnabled ? (
        screenType === 'dm' ? (
          <GroupTabSwitcher
            tabTitles={[t('messages'), t('mentions')]}
            tabValues={['messages', 'mentions']}
            defaultTab={dmMessagesType}
            onPress={val => setDmMessagesType(val)}
          />
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
      <ConversationsView
        conversations={sortedConversations}
        screenType={screenType}
        tabType={screenType === 'dm' ? dmMessagesType : undefined}
        user={user}
        onPressConversation={onPressConversation}
        role={user?.role ?? 'helper'}
        onCreateConversation={onCreateConversation}
        allowCreateTypes={user?.chat?.allowCreate}
        onLongPress={onLongPress}
        chatEnabled={chatEnabled}
        onRefresh={onRefresh}
        onGoToStarRecipients={onGoToStarRecipients}
        urgentUnreadConversationIds={urgentUnreadConversationIds}
      />
    </StatusBoundary>
  )
}
